import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import $ from "jquery";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Skeleton,
  Chip,
  CircularProgress,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import { EventsTimeRangePicker } from "../../../common/Greens";
import { StyledMenu } from "../../application/analytics/styled";
import { getBotType, PeriodItemComponent, getResStatusString } from "../../application/analytics/common";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import SiteScopeGate from "../../../common/SiteScopeGate";
import PermissionGate from "../../../guards/PermissionGate";
import { formatDate } from "../../../../utils/format";
import { tableHeadSx, tableBodySx } from "./WAFEvent";
import { getDefaultEventListingCustomDateRange } from "./eventTimeRange";

import useEvent from "../../../../hooks/user/useEvent";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import useBMConfig from "../../../../hooks/user/useBMConfig";
import useHome from "../../../../hooks/user/useHome";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import EventFilterBar from "./EventFilterBar";
import EventDetailDrawer from "./EventDetailDrawer";
import EventsActivityHeatmap from "./WAFEventsHeatmap";
import { readDashboardEventDeepLinkState } from "./dashboardEventNavigation";
import { buildConditionsFromFilters, getActionFromFilters, withSearchCondition } from "./eventFilterConditions";
import { getBotEventDetectors } from "./botEventDrawerData";

const SEARCH_DEBOUNCE_MS = 500;

const getBotStatusString = (res_code) => {
  const status = getResStatusString(res_code);
  return status === "Passed" ? "Detect" : status;
};

/** v2 BM rows carry `action` (block/challenge/allow); status alone often yields "Undefined". */
const getBotEventActionLabel = (item) => {
  const raw = item?.action;
  if (raw != null && String(raw).trim() !== "") {
    const s = String(raw).trim().toLowerCase();
    if (s === "block" || s === "blocked") return "Blocked";
    if (s === "challenge" || s === "challenged") return "Challenged";
    if (s === "allow" || s === "passed" || s === "pass") return "Detect";
    if (s === "detect" || s === "detected" || s === "log" || s === "logged") return "Detect";
    return String(raw)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const fromStatus = getBotStatusString(item?.resStatus);
  if (fromStatus === "Undefined") {
    const rc = Number(item?.resStatus);
    if (!Number.isFinite(rc) || rc === 0) return "Detect";
  }
  return fromStatus;
};

function SkeletonContent(props) {
  const { rowsPerPage } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = [0, 1, 2, 3, 4, 5, 6];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "11px",
                }}
              />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
};
const BotEventPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardDeepLink = useMemo(() => readDashboardEventDeepLinkState(location.state), [location.state]);
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWafEventController,
  } = useAuth();
  const { PERMISSIONS } = usePermissions();
  const {
    botEvents,
    botEvent,
    time_range,
    total_bot_events_count,
    // from,
    // rows_per_page,
    // action,
    getOnlyBotEvents,
    getBotEvents,
    getBotEvent,
    getBotEventStats,
    // conditions,
    botEventLoading,
  } = useEvent();
  const { getTopBotEventsInfo } = useBMConfig();
  const { curProjectId } = useHome();
  const { apiSiteId: siteID } = useGlobalSite();

  // Check if user has permission to view bot events

  const [timeRange, setTimeRange] = useState(() => {
    if (dashboardDeepLink?.timeRange) return dashboardDeepLink.timeRange;
    return time_range?.period || "7d";
  });
  const [customRangeConfirm, setCustomRangeConfirm] = useState(
    () => dashboardDeepLink?.customRangeConfirm ?? false
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(() => {
    if (dashboardDeepLink?.customDateRange) return dashboardDeepLink.customDateRange;
    return getDefaultEventListingCustomDateRange();
  });
  const [activeFilters, setActiveFilters] = useState(() => dashboardDeepLink?.initialFilters ?? []);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    if (!dashboardDeepLink) return;
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isFilteringById, setIsFilteringById] = useState(false);
  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const getTimeRange = () => {
    if (timeRange === "custom") {
      const now = new Date();
      const timeZone =
        now.getTimezoneOffset() >= 0
          ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
          : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    } else {
      return { period: timeRange };
    }
  };

  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatBotTypeToken = (token) =>
    String(token)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getBotTypeLabel = (eventItem) => {
    const primary = eventItem?.bot_type || eventItem?.attack_type || eventItem?.raw?.bot_type;
    if (primary && String(primary).trim()) {
      return formatBotTypeToken(primary.trim());
    }
    const ja4 = eventItem?.ja4 && String(eventItem.ja4).trim();
    if (ja4) {
      const short = ja4.length > 24 ? `${ja4.slice(0, 20)}…` : ja4;
      return `JA4 · ${short}`;
    }
    const httpVer = eventItem?.http_version && String(eventItem.http_version).trim();
    if (httpVer) return formatBotTypeToken(httpVer);
    const msg = eventItem?.bot_rule_msg && String(eventItem.bot_rule_msg).trim();
    if (msg) return formatBotTypeToken(msg);
    if (eventItem?.bot_score !== undefined && eventItem?.bot_score !== null && eventItem.bot_score !== 0) {
      return getBotType(eventItem.bot_score);
    }
    return "—";
  };

  const apiConditions = useMemo(
    () => withSearchCondition(
      buildConditionsFromFilters(activeFilters.filter((f) => f.id !== "id" && f.id !== "status"), "bot"),
      query
    ),
    [activeFilters, query]
  );

  const apiAction = useMemo(
    () => getActionFromFilters(activeFilters),
    [activeFilters]
  );

  const heatmapConditions = useMemo(
    () => apiConditions,
    [apiConditions]
  );

  const heatmapAction = useMemo(
    () => apiAction,
    [apiAction]
  );

  const heatmapTimeRange = useMemo(() => {
    if (timeRange === "custom" && customRangeConfirm === false) return null;
    return getTimeRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customDateRange, customRangeConfirm]);

  const getEventsWithFillter = async () => {
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    
    // Check if there's an ID filter - use dedicated GET endpoint
    const idFilter = activeFilters.find((f) => f.id === "id" && f.value);
    if (idFilter) {
      setIsFilteringById(true);
      setAllEvents([]);
      setHasMore(false);
      isInitialLoadRef.current = false;
      await getBotEvent(idFilter.value);
      return;
    }
    
    setIsFilteringById(false);
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    getTopBotEventsInfo(setWafEventController, siteID, getTimeRange());
    getBotEvents(setWafEventController, siteID, getTimeRange(), ITEMS_PER_PAGE, 0, apiAction, apiConditions);
    getBotEventStats(setWafEventController, siteID, getTimeRange(), apiConditions);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    getEventsWithFillter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, siteID, customDateRange, timeRange, activeFilters, query]); 

  const [allEvents, setAllEvents] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const ITEMS_PER_PAGE = 10;

  React.useEffect(() => {
    if (botEvents !== null && !isFilteringById) {
      if (botEvents.length === 0 && allEvents.length > 0 && isInitialLoadRef.current) {
        setAllEvents([]);
        setHasMore(true);
        isInitialLoadRef.current = true;
      } else if (botEvents.length > 0) {
        if (isInitialLoadRef.current) {
          const initialEvents = botEvents.slice(0, ITEMS_PER_PAGE);
          setAllEvents(initialEvents);
          setHasMore(initialEvents.length === ITEMS_PER_PAGE && botEvents.length >= ITEMS_PER_PAGE && initialEvents.length < total_bot_events_count);
          isInitialLoadRef.current = false;
        } else {
          setAllEvents((prev) => {
            const newEventsToAdd = botEvents.slice(0, ITEMS_PER_PAGE);
            const newEvents = [...prev, ...newEventsToAdd];
            setHasMore(newEvents.length < total_bot_events_count);
            return newEvents;
          });
        }
      }
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botEvents, total_bot_events_count, isFilteringById]);

  // Handle single event fetch (when filtering by ID)
  React.useEffect(() => {
    if (isFilteringById) {
      if (botEvent) {
        // Normalize single event structure to match list format
        const normalizedEvent = { ...botEvent };
        if (normalizedEvent.type && typeof normalizedEvent.type === "object" && !Array.isArray(normalizedEvent.type)) {
          normalizedEvent.type = [normalizedEvent.type];
        }
        setAllEvents([normalizedEvent]);
      } else {
        setAllEvents([]);
      }
    }
  }, [botEvent, isFilteringById]);

  React.useEffect(() => {
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
  }, [siteID, customDateRange, timeRange, curProjectId, activeFilters, query]);

  React.useEffect(() => {
    if (!hasMore || isLoadingMore || allEvents.length < ITEMS_PER_PAGE) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = document.querySelector('.ps');
    const root = perfectScrollbar || null;
    const rootMargin = '200px';

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allEvents.length >= ITEMS_PER_PAGE && allEvents.length < total_bot_events_count) {
            setIsLoadingMore(true);
            const nextFrom = allEvents.length;
            const currentTimeRange = getTimeRange();
            getOnlyBotEvents(siteID, currentTimeRange, ITEMS_PER_PAGE, nextFrom, apiAction, apiConditions);
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, allEvents.length, siteID, timeRange, customDateRange, apiAction, apiConditions, getOnlyBotEvents, total_bot_events_count]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  const handleFilterSimilar = (filterType, event) => {
    if (!event) return;
    
    let filterId = null;
    let filterValue = null;
    let filterLabel = null;
    
    switch (filterType) {
      case "ip":
        filterId = "src_ip";
        filterValue = event.src_ip;
        filterLabel = "IP Address";
        break;
      case "country":
        filterId = "country";
        filterValue = event.country_iso_code || event.country_name;
        filterLabel = "Country";
        break;
      case "status":
        filterId = "status";
        filterValue = getBotStatusString(event.resStatus);
        filterLabel = "Status";
        break;
      default:
        return;
    }
    
    if (!filterId || !filterValue) return;
    
    // Check if filter already exists
    const existingFilterIndex = activeFilters.findIndex((f) => f.id === filterId);
    
    if (existingFilterIndex >= 0) {
      // Update existing filter
      const newFilters = [...activeFilters];
      newFilters[existingFilterIndex] = {
        ...newFilters[existingFilterIndex],
        value: filterValue,
      };
      handleFiltersChange(newFilters);
    } else {
      // Add new filter
      const newFilters = [
        ...activeFilters,
        {
          id: filterId,
          label: filterLabel,
          value: filterValue,
        },
      ];
      handleFiltersChange(newFilters);
    }
  };
  return (
    <PermissionGate permission={PERMISSIONS.BOT_READ} resourceLabel="Bot events">
    <>
      <Helmet title="Bot Event" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader title="Bot Event" sx={{ pb: 0 }} />
        <SiteScopeGate mode="any">
          {() => (
            <>
        <Stack spacing={2}>
          <EventFilterBar
            eventType="bot"
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            events={allEvents}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search host, URI, IP, UA…"
            searchPending={search.trim() !== query}
            timeRangeComponent={
              <EventsTimeRangePicker
                selectPeriod={selectPeriod}
                selectCustomDateRange={selectCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
            }
            onRefresh={getEventsWithFillter}
            refreshing={botEventLoading}
          />
        </Stack>
        <EventsActivityHeatmap
          endpoint="log/bot_events_heatmap"
          siteId={siteID}
          timeRange={heatmapTimeRange}
          conditions={heatmapConditions}
          action={heatmapAction}
          customDateRange={customDateRange}
          exactSeries
          // LE-432: Period Total = events list total (same WHERE as Top card drill-down)
          periodTotalOverride={
            Array.isArray(heatmapConditions) && heatmapConditions.length > 0 && !botEventLoading
              ? total_bot_events_count
              : undefined
          }
        />
        <Stack>
          <Box ref={tableContainerRef} spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography sx={tableHeadSx}>Event ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Activity</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Source IP</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Bot type</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography sx={tableHeadSx}>Detectors</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 230 }}>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 48 }}>
                    {/* Eye view column — not labeled "Action" (that name is used by the bot Action data column). */}
                    <Typography sx={tableHeadSx} />
                  </TableCell>
                </TableRow>
              </TableHead>
              {botEvents === null && allEvents.length === 0 ? (
                <SkeletonContent rowsPerPage={ITEMS_PER_PAGE} />
              ) : allEvents.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography textAlign="center" sx={tableBodySx}>
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {allEvents?.map((item, index) => {
                    const actionLabel = getBotEventActionLabel(item);
                    return (
                    <TableRow key={`detail_data_${item?.id || index}`} onClick={() => handleEventClick(item)} sx={{ cursor: "pointer" }}>
                      <TableCell>
                        <Typography
                          sx={{
                            ...tableBodySx,
                            fontFamily: "monospace",
                            color: "#1E40AF",
                            wordBreak: "break-all",
                          }}
                        >
                          {item?.id || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{item?.host_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography sx={tableBodySx}>
                            {item?.resStatus &&
                            Number(item.resStatus) !== 0
                              ? `${item.resStatus} `
                              : ""}
                            {item?.method}
                          </Typography>
                          {item?.uri?.length > 36 ? (
                            <Tooltip title={item?.uri} arrow>
                              <Typography sx={{ ...tableBodySx, color: colors.primary[300] }}>{item?.uri?.substr(0, 36) + " ..."}</Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ ...tableBodySx, color: colors.primary[300] }}>{item?.uri}</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems={"center"}>
                          <ReactCountryFlag
                            countryCode={item?.country_iso_code}
                            svg
                            title={item?.country_name}
                            style={{
                              width: "26px",
                              height: "18px",
                            }}
                          />
                          <Typography sx={{ ...tableBodySx, pl: 2 }}>{item?.src_ip}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getBotTypeLabel(item) === "—" ? (
                          <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>—</Typography>
                        ) : (
                        <Chip
                          size="small"
                          label={getBotTypeLabel(item)}
                          sx={{ px: 1, background: colors.yellow[100], borderRadius: "30px", ...tableBodySx }}
                        />
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const detectors = getBotEventDetectors(item);
                          if (!detectors.length) {
                            return <Typography sx={{ ...tableBodySx, color: colors.neutral[400] }}>—</Typography>;
                          }
                          return (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {detectors.slice(0, 4).map((det) => (
                                <Chip
                                  key={det}
                                  size="small"
                                  label={det}
                                  sx={{
                                    px: 0.5,
                                    height: 20,
                                    fontSize: 11,
                                    borderRadius: "10px",
                                    background: colors.neutral[100],
                                    color: colors.neutral[700],
                                    border: `1px solid ${colors.neutral[200]}`,
                                  }}
                                />
                              ))}
                              {detectors.length > 4 && (
                                <Tooltip title={detectors.slice(4).join(", ")} arrow>
                                  <Chip
                                    size="small"
                                    label={`+${detectors.length - 4}`}
                                    sx={{ height: 20, fontSize: 11, borderRadius: "10px", background: colors.neutral[100], color: colors.neutral[700] }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={actionLabel}
                          sx={{
                            px: 1,
                            borderRadius: "30px",
                            ...tableBodySx,
                            background: actionLabel === "Blocked" ? "#FEE2E2" :
                                        actionLabel === "Challenged" ? "#FEF3C7" : "#D1FAE5",
                            color: actionLabel === "Blocked" ? "#B91C1C" :
                                   actionLabel === "Challenged" ? "#92400E" : "#065F46",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{formatDate(item?.timestamp) || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        {/* <Stack
                          alignItems={"flex-end"}
                          onClick={() =>
                            navigate(
                              `/v2/detection/bot_event/view/${DetectionFilterCategory.TopWAFDetectionsByCountry}/${
                                item?.id
                              }?data=${encodeURIComponent(item?.country_iso_code)}`
                            )
                          }
                          sx={{ cursor: "pointer" }}
                        > */}
                          <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                        {/* </Stack> */}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              )}
            </Table>
            {hasMore && allEvents.length >= ITEMS_PER_PAGE && (
              <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />
            )}
            {isLoadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!hasMore && allEvents.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>
                  No more events to load
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <StyledMenu
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <PeriodItemComponent selectPeriod={selectPeriod} />
      </StyledMenu>
      <EventDetailDrawer
        open={drawerOpen} 
        onClose={handleDrawerClose} 
        event={selectedEvent} 
        event_Type="bot"
        onFilterSimilar={handleFilterSimilar}
      />
    </>
    </PermissionGate>
  );
};
export default BotEventPage;
